import { trackEvent } from '../utils/usage.js'
// Online play: getting two browsers talking to each other.
//
// Firebase is only the introduction. The host puts a four-letter room (a
// word - see roomCode.js) under `rooms/<CODE>`; a guest writes its WebRTC
// offer under `rooms/<CODE>/guests/<id>`; the host writes back an answer;
// from then on the two browsers talk directly over WebRTC data channels
// and Firebase is out of the loop. Offers and answers carry their network
// candidates inside them (we wait for gathering to finish before writing),
// so there is no separate candidate plumbing.
//
// Two channels per guest:
//  - `ctl`  - reliable and ordered: lobby changes, button presses, events.
//  - `snap` - unreliable and unordered: the 60-a-second picture of the
//             well. A late one is worthless (a newer one is right behind
//             it), so it is never re-sent and never holds up the next.

import {
  ref as dbRef, set, get, onValue, onChildAdded, onDisconnect, remove, serverTimestamp,
} from 'firebase/database';
import { signInAnonymously } from 'firebase/auth';
import { db, auth } from '../firebase';
import { randomWord, randomLetters, cleanCode } from './roomCode';
import { clientId } from './rejoin';

export { cleanCode };

const STUN = [
  { urls: ['stun:stun.l.google.com:19302', 'stun:stun1.l.google.com:19302'] },
];
// Relay (TURN) logins from our own Lambda (aws-lambda/couch-blocks-turn.mjs).
// STUN alone only works when both networks allow a direct link; the relay
// carries the game when they don't. If the Lambda is unset, slow or down,
// rooms go on with STUN alone, as they always did.
const TURN_URL = process.env.VUE_APP_TURN_URL;
const TURN_WAIT_MS = 3000;
const TURN_KEEP_MS = 60 * 60 * 1000; // the logins last four hours
let turnCache = null;

async function iceServers() {
  if (!TURN_URL) return STUN;
  if (turnCache && Date.now() - turnCache.at < TURN_KEEP_MS) return [...STUN, ...turnCache.servers];
  try {
    const res = await fetch(TURN_URL, { signal: AbortSignal.timeout(TURN_WAIT_MS) });
    const { iceServers: servers } = await res.json();
    if (!res.ok || !Array.isArray(servers)) return STUN;
    if (servers.length) turnCache = { at: Date.now(), servers };
    return [...STUN, ...servers];
  } catch {
    return STUN;
  }
}
const GATHER_TIMEOUT_MS = 4000;
const JOIN_TIMEOUT_MS = 20000;
// "disconnected" is often a blip (wifi hiccup, a phone switching networks)
// that WebRTC recovers from by itself; only call it gone if it lasts.
const DROP_GRACE_MS = 5000;

export const onlineAvailable = () => Boolean(db && auth);

async function ensureSignedIn() {
  if (!auth.currentUser) await signInAnonymously(auth);
  return auth.currentUser.uid;
}

// Resolves once the description carries the candidates worth sending.
// Waiting for gathering to finish can take several seconds (the browser
// keeps probing interfaces that will never answer), so: done when gathering
// completes, or shortly after the first public-address candidate from STUN
// arrives - that is the one a computer in another city needs - or after a
// few seconds regardless.
function gathered(pc) {
  if (pc.iceGatheringState === 'complete') return Promise.resolve();
  return new Promise((resolve) => {
    let finished = false;
    const done = () => {
      if (finished) return;
      finished = true;
      pc.removeEventListener('icegatheringstatechange', check);
      pc.removeEventListener('icecandidate', onCandidate);
      resolve();
    };
    const check = () => { if (pc.iceGatheringState === 'complete') done(); };
    const onCandidate = (e) => {
      if (!e.candidate) { done(); return; }
      if (e.candidate.type === 'srflx') setTimeout(done, 300);
    };
    pc.addEventListener('icegatheringstatechange', check);
    pc.addEventListener('icecandidate', onCandidate);
    setTimeout(done, GATHER_TIMEOUT_MS);
  });
}

// Calls onGone once the connection is really gone: failed or closed at
// once, disconnected only if it hasn't come back after DROP_GRACE_MS.
function watchConnection(pc, onGone) {
  let timer = null;
  pc.onconnectionstatechange = () => {
    const state = pc.connectionState;
    if (state === 'disconnected') {
      timer ||= setTimeout(() => {
        timer = null;
        if (pc.connectionState === 'disconnected') onGone();
      }, DROP_GRACE_MS);
      return;
    }
    clearTimeout(timer);
    timer = null;
    if (state === 'failed' || state === 'closed') onGone();
  };
}

function wireChannel(channel, peer, onMessage) {
  channel.onmessage = (e) => {
    try { onMessage(peer, JSON.parse(e.data)); } catch { /* a garbled message is dropped */ }
  };
}

function makePeer(pc, id) {
  const peer = {
    id,
    pc,
    ctl: null,
    snap: null,
    send(msg) { if (peer.ctl?.readyState === 'open') peer.ctl.send(JSON.stringify(msg)); },
    sendSnap(msg) { if (peer.snap?.readyState === 'open') peer.snap.send(JSON.stringify(msg)); },
    close() { try { pc.close(); } catch { /* already gone */ } },
  };
  return peer;
}

/**
 * Host a room. Returns { code, close() }.
 * handlers: onPeerOpen(peer), onPeerClosed(peer), onMessage(peer, msg)
 * Each peer carries `client`, the joining tab's own id (see rejoin.js).
 */
export async function hostRoom(handlers) {
  trackEvent('room-hosted')
  const uid = await ensureSignedIn();
  let code = null;
  // A word if one of a few tries is free, random letters if not.
  for (let tries = 0; tries < 12 && !code; tries += 1) {
    const candidate = tries < 8 ? randomWord() : randomLetters();
    // eslint-disable-next-line no-await-in-loop -- try codes one at a time
    const existing = await get(dbRef(db, `rooms/${candidate}`));
    if (!existing.exists()) code = candidate;
  }
  if (!code) throw new Error('Could not find a free room code.');

  const roomRef = dbRef(db, `rooms/${code}`);
  await set(roomRef, { host: uid, createdAt: serverTimestamp() });
  // Closing the tab takes the room with it.
  onDisconnect(roomRef).remove();

  const peers = new Map();
  const claimed = new Set();
  // Fetched now so the first guest isn't kept waiting for it.
  iceServers();
  const stop = onChildAdded(dbRef(db, `rooms/${code}/guests`), async (snap) => {
    const guest = snap.val();
    if (!guest?.offer || guest.answer || claimed.has(snap.key)) return;
    claimed.add(snap.key); // so a repeat event can't start a second link while the relay logins load
    const pc = new RTCPeerConnection({ iceServers: await iceServers() });
    const peer = makePeer(pc, snap.key);
    peer.client = typeof guest.client === 'string' ? guest.client : null;
    peers.set(snap.key, peer);
    pc.ondatachannel = (e) => {
      peer[e.channel.label] = e.channel;
      wireChannel(e.channel, peer, handlers.onMessage);
      if (e.channel.label === 'ctl') e.channel.onopen = () => handlers.onPeerOpen(peer);
      if (e.channel.label === 'ctl' && e.channel.readyState === 'open') handlers.onPeerOpen(peer);
    };
    watchConnection(pc, () => {
      if (!peers.has(peer.id)) return;
      peers.delete(peer.id);
      handlers.onPeerClosed(peer);
    });
    await pc.setRemoteDescription({ type: 'offer', sdp: guest.offer });
    await pc.setLocalDescription(await pc.createAnswer());
    await gathered(pc);
    await set(dbRef(db, `rooms/${code}/guests/${snap.key}/answer`), pc.localDescription.sdp);
  });

  return {
    code,
    peers,
    close() {
      stop();
      peers.forEach((p) => p.close());
      peers.clear();
      remove(roomRef).catch(() => {});
    },
  };
}

/**
 * Join a room by code. Resolves with the connected host peer once both
 * channels are open; rejects with a message a person can read.
 * handlers: onMessage(peer, msg), onClosed()
 */
export async function joinRoom(rawCode, handlers) {
  trackEvent('room-joined')
  const code = cleanCode(rawCode);
  if (code.length !== 4) throw new Error('Room codes are four letters.');
  const uid = await ensureSignedIn();
  const room = await get(dbRef(db, `rooms/${code}`));
  if (!room.exists()) throw new Error(`No game called ${code} - check the code with the host.`);

  const pc = new RTCPeerConnection({ iceServers: await iceServers() });
  const peer = makePeer(pc, 'host');
  peer.ctl = pc.createDataChannel('ctl', { ordered: true });
  peer.snap = pc.createDataChannel('snap', { ordered: false, maxRetransmits: 0 });
  wireChannel(peer.ctl, peer, handlers.onMessage);
  wireChannel(peer.snap, peer, handlers.onMessage);

  await pc.setLocalDescription(await pc.createOffer());
  await gathered(pc);
  const guestId = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;
  const guestRef = dbRef(db, `rooms/${code}/guests/${guestId}`);
  await set(guestRef, {
    uid, client: clientId(), offer: pc.localDescription.sdp, at: serverTimestamp(),
  });
  onDisconnect(guestRef).remove();

  return new Promise((resolve, reject) => {
    let answered = false;
    let joined = false;
    // Two different failures, told apart so the person knows what to try:
    // no answer at all means the host's game isn't picking up (closed tab,
    // asleep, stuck); an answer but no link means the networks between
    // the two computers wouldn't connect.
    const fail = () => {
      if (joined) return;
      joined = true;
      clearTimeout(timer);
      stop();
      pc.close();
      remove(guestRef).catch(() => {});
      trackEvent(answered ? 'join-failed-network' : 'join-failed-no-answer');
      reject(new Error(answered
        ? "Couldn't reach the host's computer. Some networks block direct connections - try another network, or ask the host to try."
        : "The host's game didn't answer. Check the host still has the room open on their screen, then try again."));
    };
    const timer = setTimeout(fail, JOIN_TIMEOUT_MS);
    const stop = onValue(dbRef(db, `rooms/${code}/guests/${guestId}/answer`), async (s) => {
      if (!s.exists() || pc.remoteDescription) return;
      answered = true;
      await pc.setRemoteDescription({ type: 'answer', sdp: s.val() });
    });
    const opened = () => {
      if (joined || peer.ctl.readyState !== 'open' || peer.snap.readyState !== 'open') return;
      joined = true;
      clearTimeout(timer);
      stop();
      // The introduction is done; nothing in Firebase is needed any more.
      remove(guestRef).catch(() => {});
      resolve({ code, peer });
    };
    peer.ctl.onopen = opened;
    peer.snap.onopen = opened;
    // Gone before it ever opened is a failed join, not a host leaving.
    watchConnection(pc, () => (joined ? handlers.onClosed() : fail()));
  });
}
